import { Box, Button, HStack } from "@chakra-ui/react";
import GuidedTour from "../walkthrough/components/GuidedTour";
import { COURT_CONFIG_TOUR_ID, courtConfigTourSteps } from "../walkthrough/tours/court-config-tour";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageCard from "../../components/page-card";
import SegmentedControl from "../../components/segmented-control";
import { translate, TranslationKeys } from "../../i18n";
import { CoachingSessionStatus } from '../../models/coach/dto/coaching-session.dto';
import { CourtAvailabilityDto } from "../../models/court/dto/court-availability.dto";
import { CourtTemporaryBlockadeDto } from "../../models/court/dto/court-temporary-blockade.dto";
import { GetCourtCalendarOutputDto } from "../../models/court/dto/get-court-calendar.dto";
import { EventStatus } from '../../models/event/dto/event.dto';
import { paramFromWindowLocation } from "../../utils/url-parser";
import { CourtConfigurationPage } from "../court-configuration/pages/CourtConfigurationPage";
import { useGetCourt } from '../court-management/hooks/useGetCourt';
import ErrorState from "../../components/states/ErrorState";
import InlineErrorState from "../../components/states/InlineErrorState";
import { classifyRead, READ_FAILED_I18N_KEY } from "../_shared/readState";
import AddAvailabilitiesModal from "./components/AddAvailabilitiesModal";
import EditCourtPricingGridModal from "../court-pricing-grid/components/EditCourtPricingGridModal";
import { useIsOrgManager } from "../auth/roles";
import { resolveViewMode, ViewMode } from "./view-mode";
import CancelCourtDayModal from "./components/CancelCourtDayModal";
import { courtBlockadeReasonEnumToString } from "./components/BlockadeReasonPicker";
import CourtDetailsCard from "./components/CourtDetailsCard";
import CourtPricingHeatmap from "./components/CourtPricingHeatmap";
import CreateBlockadeOrCourtAvailabilityModal, { SelectedTimeSlotForBlockadeProps } from './components/CreateBlockadeOrCourtAvailabilityModal';
import MonthlyCourtCalendar from "./components/MonthlyCalendar";
import SelectedBlockadeModal from "./components/SelectedBlockadeModal";
import SelectedCourtAvailabilityModal from "./components/SelectedCourtAvailabilityModal";
import { GET_COURT_CALENDER_QUERY_KEY, useGetCourtCalendar } from "./hooks/useGetCourtCalendar";

export enum CalendarEventType {
    Game,
    Blockade,
    Event,
    CoachingSession
}

export default function CourtDetailsPage() {
    const isOrgManager = useIsOrgManager();
    const courtId = Number.parseInt(paramFromWindowLocation('court'));
    // A missing/garbled `court` URL param yields NaN. Guard every request so we
    // never fire `courts/NaN` (Sentry MYSPORTSET-API-E9 / -EA — the whole page
    // used to 500 on load in that state).
    const isValidCourtId = Number.isInteger(courtId);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dateBoundary, setDateBoundary] = useState(() => getInitialDateBoundary());

    const tabParam = searchParams.get('tab');
    // See `resolveViewMode`: `configuration` is reachable only by hand-typing
    // the query string and is admin-only work, so it falls back to `details`
    // for a coach.
    const viewMode = resolveViewMode(tabParam, isOrgManager);

    const setViewMode = useCallback((mode: ViewMode) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (mode === ViewMode.details) {
                next.delete('tab');
            } else {
                next.set('tab', mode);
            }
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // The calendar read beside the court read below. Run 110 classified the
    // court; this one kept dropping both of its flags, and what a refusal draws
    // is NOT merely "an empty month" — that was the ranking this run came to
    // check and it was too kind by two steps.
    //
    // 1. Every derivation below is `(courtMonthlyCalendar?.X || [])`, so a
    //    refused read paints a court with no games, no coaching sessions, no
    //    events, no blockades and no availabilities: a completely free court,
    //    stated confidently, over a court that may be fully booked.
    // 2. Clicking any cell of that invented empty month calls
    //    `onTimeSlotSelected` and opens `CreateBlockadeOrCourtAvailabilityModal`
    //    — so the page OFFERS a write on the strength of a read that failed, and
    //    the write lands on top of whatever is really there. R127-1's shape
    //    (`TimeWindowForm`) on a surface where the invented state is a whole
    //    month rather than one row.
    // 3. The write then CRASHES the page. `onAvailabilitiesCreated`,
    //    `onBlockadeDeleted` and `onCourtAvailabilityDeleted` all reach into
    //    `newData.<list>` inside `setQueryData`, and `newData` is `undefined`
    //    when the query holds no data — which is exactly the refused-read world,
    //    and also the ordinary in-flight world for a range the operator has just
    //    navigated to. `onBlockadeCreated` is the only one of the four that
    //    guards it (`if (!newData) return newData`), which is how we know the
    //    hazard was known; the other three were written without it. So a
    //    SUCCESSFUL availability creation threw a TypeError out of the updater
    //    and the operator's work appeared to fail after the server had accepted
    //    it. Fixed at both ends: the guard is on all four, and the affordance
    //    that reaches them is closed while the month is unknown.
    //
    // `unreadable` is `isError && !data` (R96-3), so a failed background refetch
    // over a month already on screen keeps that month and keeps its actions —
    // the guard closes only when there is genuinely nothing to reason about.
    const {
        data: courtMonthlyCalendar,
        isError: isCalendarError,
        isInitialLoading: isCalendarInitialLoading,
    } = useGetCourtCalendar(courtId, dateBoundary.start, dateBoundary.end, isValidCourtId);
    const calendarReadState = classifyRead(isCalendarError, isCalendarInitialLoading, courtMonthlyCalendar);
    const isCalendarUnreadable = calendarReadState === 'unreadable';
    // `GET courts/:id` can answer 403 (an operator who lost admin on the org),
    // 404 (a deleted court, a stale bookmark) and 5xx. Every branch below
    // renders on `Boolean(court) &&`, so a refused read used to leave this page
    // LOOKING ALIVE while stating nothing: the calendar comes from a different
    // query and still drew, under a SegmentedControl whose first tab label is
    // `court?.name` — i.e. an unlabelled, still-clickable tab — with no court
    // details, no pricing card, and no cancel-day/add-availability actions.
    //
    // The sharpest consequence is two hundred lines below and this file already
    // documents it: `SelectedBlockadeModal` is passed
    // `court?.location?.organizationId`, and the comment there records that
    // without it the modal treats a PENDING_PAYMENT blockade as "organization
    // still loading" forever — permanent spinner banner, no cancel, no
    // copy-payment-link recovery. A refused court read silently re-creates the
    // exact incident that comment was written to fix.
    //
    // `staleTime`/`cacheTime: Infinity` on this query mean one failed read
    // stands for the whole session rather than for one paint.
    const { data: court, isError: isCourtError, isInitialLoading: isCourtInitialLoading, refetch: refetchCourt } = useGetCourt(courtId);
    const courtReadState = classifyRead(isCourtError, isCourtInitialLoading, court);

    const courtAvailabilities = useMemo(() => {
        if (!courtMonthlyCalendar?.availabilities) return [];
        return courtMonthlyCalendar.availabilities;
    }, [courtMonthlyCalendar]);


    const events = useMemo(() => {
        const gamesAsEvents = (courtMonthlyCalendar?.games || []).map(g => {
            const orderIndicator = (g.orderCount ?? 0) > 0 ? ' 📦' : '';
            return {
                start: new Date(g.startTime),
                end: new Date(new Date(g.startTime).getTime() + g.duration),
                title: `Match (${g.organiser?.firstName} ${g.organiser?.lastName})${orderIndicator}`,
                eventType: CalendarEventType.Game,
                game: g
            }
        });
        const coachingSessionsAsEvents = (courtMonthlyCalendar?.coachingSessions || []).map(g => {
            const isCanceled = g.status === CoachingSessionStatus.CANCELED;
            const titlePrefix = isCanceled ? '[CANCELED] ' : '';
            return {
                start: new Date(g.startTime),
                end: new Date(g.endTime),
                title: `${titlePrefix}Coaching (${g.user?.firstName} ${g.user?.lastName}) - [${g.court?.name}]`,
                eventType: CalendarEventType.CoachingSession,
                coachingSession: g
            }
        });

        const blockadesAsEvents = (courtMonthlyCalendar?.blockades || []).map(blockade => {
            return {
                start: new Date(blockade.startTime),
                end: new Date(blockade.endTime),
                title: `Block - [${courtBlockadeReasonEnumToString(blockade.reason)}]${blockade.description ? ' - ' + blockade.description : ''}`,
                eventType: CalendarEventType.Blockade,
                blockade
            }
        });

        const eventsAsEvents = (courtMonthlyCalendar?.events || []).map(event => {
            const isCanceled = event.status === EventStatus.CANCELLED;
            const titlePrefix = isCanceled ? '[CANCELED] ' : '';
            const courtsNames = event.courts?.map(c => c.name).join(', ') || '';
            return {
                start: new Date(event.startTime),
                end: new Date(new Date(event.startTime).getTime() + event.duration),
                title: `${titlePrefix}Event: ${event.name}${courtsNames ? ` [${courtsNames}]` : ''}`,
                eventType: CalendarEventType.Event,
                event
            }
        });

        return [...blockadesAsEvents, ...gamesAsEvents, ...coachingSessionsAsEvents, ...eventsAsEvents];
    }, [courtMonthlyCalendar]);

    const backgroundEvents = useMemo(() => {
        return (courtMonthlyCalendar?.availabilities || []).map(availability => {
            let translationKey: TranslationKeys;
            if (availability.isCoachingAvailable && availability.isGameAvailable) {
                translationKey = 'calendar.available_for_games_and_coaching';
            } else if (availability.isGameAvailable) {
                translationKey = 'calendar.available_for_games';
            } else {
                translationKey = 'calendar.available_for_coaching';
            }
            const title = translate(translationKey);

            return {
                availability,
                title,
                start: new Date(availability.startTime),
                end: new Date(availability.endTime),
            }
        });
    }, [courtMonthlyCalendar]);

    const queryClient = useQueryClient();
    const onBlockadeCreated = (blockade: CourtTemporaryBlockadeDto) => {
        queryClient.setQueryData<GetCourtCalendarOutputDto>(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end), (newData) => {
            if (!newData) return newData;
            return { ...newData, blockades: [...newData.blockades, blockade] };
        });
        queryClient.invalidateQueries(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end));
    };
    const onAvailabilitiesCreated = (items: CourtAvailabilityDto[]) => {
        queryClient.setQueryData<GetCourtCalendarOutputDto>(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end), (newData) => {
            // `newData` is undefined whenever this month's read holds no data —
            // a refused read, or a range the operator navigated to while the
            // fetch was still in flight. `newData.availabilities` threw a
            // TypeError out of the updater there, i.e. a successful server-side
            // create surfaced to the operator as a broken page. The
            // `invalidateQueries` below is what actually reconciles the cache,
            // so returning `newData` untouched loses nothing.
            if (!newData) return newData;
            return { ...newData, availabilities: [...newData.availabilities, ...items] };
        });
        queryClient.invalidateQueries(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end));
    };
    const onBlockadeDeleted = (blockadeId: string) => {
        queryClient.setQueryData<GetCourtCalendarOutputDto>(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end), (newData) => {
            // Same hazard as `onAvailabilitiesCreated` above: no cached month,
            // no `newData.blockades` to filter.
            if (!newData) return newData;
            return { ...newData, blockades: newData.blockades.filter(blockade => blockade.id !== blockadeId) };
        });
        queryClient.invalidateQueries(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end));
    };
    const onCourtAvailabilityDeleted = (availabilityId: string) => {
        queryClient.setQueryData<GetCourtCalendarOutputDto>(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end), (newData) => {
            // Same hazard as `onAvailabilitiesCreated` above: no cached month,
            // no `newData.availabilities` to filter.
            if (!newData) return newData;
            return { ...newData, availabilities: newData.availabilities.filter(availability => availability.id !== availabilityId) };
        });
        queryClient.invalidateQueries(GET_COURT_CALENDER_QUERY_KEY(courtId, dateBoundary.start, dateBoundary.end));
    };
    const onRangeChange = useCallback((start: Date, end: Date) => {
        setDateBoundary({ start, end })
    }, [setDateBoundary]);



    const [selectedBlockade, setSelectedBlockade] = useState<CourtTemporaryBlockadeDto>();
    const [isBlockadeModalVisible, setIsBlockadeModalVisible] = useState(false);
    const [selectedCourtAvailability, setSelectedCourtAvailability] = useState<CourtAvailabilityDto>();
    const [isCourtAvailabilityModalVisible, setIsCourtAvailabilityModalVisible] = useState(false);

    // Timeslot for creation
    const [isCreatingBlockadeModalVisible, setIsCreatingBlockadeModalVisible] = useState(false);
    const [isCancelDayModalVisible, setIsCancelDayModalVisible] = useState(false);
    const [selectedTimeSlot, setSelectedTimeslot] = useState<SelectedTimeSlotForBlockadeProps>();
    const onTimeSlotSelected = useCallback((start: Date, end: Date) => {
        // An empty cell means "this slot is free" ONLY when the month was read.
        // While it is unreadable every cell looks free, so offering the creation
        // modal here would take a blockade or an availability on the strength of
        // a read that failed and drop it on top of a real booking. The notice
        // rendered above the calendar is what explains the closed affordance —
        // a click that silently does nothing is the defect this run fixed on
        // `CampaignDetailPage`, and repeating it here would trade one wrong
        // answer for another.
        if (isCalendarUnreadable) return;
        // Blockade creation is staff-level on the API (admin OR coach), so a
        // coach gets the modal too; it hides the admin-only availability and
        // event modes for them.
        setSelectedTimeslot({
            startTime: start,
            endTime: end,
            courtId
        })
        setIsCreatingBlockadeModalVisible(true);
    }, [setSelectedTimeslot, setIsCreatingBlockadeModalVisible, courtId, isCalendarUnreadable]);

    if (!isValidCourtId) {
        // Reached when the `court` URL param is missing/garbled (e.g. a stale or
        // copied link that lost the hash query). Offer a clear way back to the
        // list instead of firing `courts/NaN` requests behind a broken page.
        return (
            <PageCard showBackButton title={translate('courts.court_not_found')}>
                <Box px={6} py={10}>
                    <Box mb={4}>{translate('courts.court_not_found_description')}</Box>
                    <Button colorScheme="brand" onClick={() => navigate('/admin/courts')}>
                        {translate('tabs.courts')}
                    </Button>
                </Box>
            </PageCard>
        );
    }

    // The court id is valid and the read still produced no court. `loading`
    // deliberately falls THROUGH to the normal render: an in-flight read
    // resolves, so it makes no permanent false claim, and blanking the calendar
    // behind a spinner would be a regression for the ordinary case. The two
    // states handled here are the ones that never resolve on their own.
    //
    // `never-issued` is unreachable today — the query is
    // `enabled: Number.isInteger(id)` and the `!isValidCourtId` arm above
    // already caught that — but it is handled rather than left to fall through,
    // because the day someone widens the gate the safe answer is "we could not
    // load this", not a half-rendered page (the `classifyElevation` precedent).
    //
    // R96-3: a BACKGROUND refetch that fails over a court already on screen
    // must not blank it. `classifyRead` gives that for free — `isUnreadable`
    // keeps a held value — so this arm is reachable only when there is nothing
    // to show.
    if (courtReadState === 'unreadable' || courtReadState === 'never-issued') {
        return (
            <PageCard showBackButton title={translate('courts.court_not_found')}>
                <ErrorState
                    message={translate('courts.court_not_found_description')}
                    onRetry={() => { refetchCourt(); }}
                />
            </PageCard>
        );
    }

    return (
        <>
            <GuidedTour tourId={COURT_CONFIG_TOUR_ID} steps={courtConfigTourSteps()} onStepChange={(stepIndex) => {
                const tabForStep: ViewMode[] = [ViewMode.details, ViewMode.calendar, ViewMode.pricing];
                const tab = tabForStep[stepIndex];
                if (tab) setViewMode(tab);
            }} />
            <CreateBlockadeOrCourtAvailabilityModal
                selectedTimeslot={selectedTimeSlot}
                courtId={courtId}
                isOrgManager={isOrgManager}
                onCourtAvailabilitiesAdded={onAvailabilitiesCreated}
                isModalVisible={isCreatingBlockadeModalVisible}
                setIsModalVisible={setIsCreatingBlockadeModalVisible}
                onBlockadeCreated={onBlockadeCreated}
                onEventCreated={() => queryClient.invalidateQueries({ queryKey: ['courtCalendar'] })}
            />
            {/* `organizationId` is not optional in practice: without it the
                modal treats a PENDING_PAYMENT blockade as "organization still
                loading" forever, so an admin clicking a pending hold from this
                page got a permanent spinner banner, no cancel action and no
                "copy payment link" recovery — the hold could only be cleared
                from /calendar, which is the copy that passes this prop. */}
            <SelectedBlockadeModal
                setIsModalVisible={setIsBlockadeModalVisible}
                isModalVisible={isBlockadeModalVisible}
                onBlockadeDeleted={onBlockadeDeleted}
                selectedBlockade={selectedBlockade}
                organizationId={court?.location?.organizationId}
            />
            <SelectedCourtAvailabilityModal
                isModalVisible={isCourtAvailabilityModalVisible}
                setIsModalVisible={setIsCourtAvailabilityModalVisible}
                selectedAvailability={selectedCourtAvailability}
                onCourtAvailabilityDeleted={onCourtAvailabilityDeleted}
            />
            {/* Single-court context: the picker is hidden and the modal fetches
                its own per-day calendar, so it no longer depends on this page's
                +/-7-day window (picking a date outside it used to preview
                "no games" and disable the confirm button). */}
            {Boolean(court) && isCancelDayModalVisible && (
                <CancelCourtDayModal
                    isOpen
                    onClose={() => setIsCancelDayModalVisible(false)}
                    courts={[court]}
                    initialCourtIds={[courtId]}
                    initialDate={new Date()}
                />
            )}
            <Box w={'100%'} display='flex' justifyContent={'flex-end'} mb={4}>
                <SegmentedControl
                    options={[
                        { id: ViewMode.details, label: court?.name },
                        { id: ViewMode.calendar, label: translate('court.calendar') },
                        { id: ViewMode.pricing, label: translate('court.pricing') },
                        //{ id: ViewMode.configuration, label: translate('court_configuration.title') }
                    ]}
                    selectedId={viewMode}
                    onChange={(id) => setViewMode(id as ViewMode)}
                />
            </Box>
            <Box data-tour="court-details" opacity={viewMode === ViewMode.details ? 1 : 0} h={viewMode === ViewMode.details ? 'auto' : 0} overflow="hidden" pointerEvents={viewMode === ViewMode.details ? 'auto' : 'none'}>
                {Boolean(court) && <CourtDetailsCard court={court} />}
            </Box>
            {Boolean(court) && viewMode === ViewMode.configuration ? <CourtConfigurationPage courtId={courtId} /> : null}
            <Box data-tour="court-pricing-heatmap" opacity={viewMode === ViewMode.pricing ? 1 : 0} h={viewMode === ViewMode.pricing ? 'auto' : 0} overflow="hidden" pointerEvents={viewMode === ViewMode.pricing ? 'auto' : 'none'}>
                {Boolean(court) && <PageCard showBackButton title={translate('court.pricing')} rightHeader={isOrgManager ? <HStack spacing={3}>
                    {/* Grid editor (beta) next to the existing modal — same endpoint,
                        different way of setting prices. See docs/epics/court-pricing-grid.md. */}
                    <EditCourtPricingGridModal court={court} />
                    <AddAvailabilitiesModal court={court} />
                </HStack> : null}>
                    {/* The heatmap's rows ARE this month's availabilities, so an
                        unreadable calendar renders 24×7 empty cells — "this court
                        is priced nowhere" — for a court whose whole week may be
                        priced. Every cell carries a price, which makes the empty
                        grid a false claim about money rather than about
                        occupancy; it is the one arm of this fix a reviewer should
                        look at first. No price is computed, read or written here:
                        the grid is either shown with real rows or replaced by a
                        notice that we could not read them. */}
                    {isCalendarUnreadable
                        ? <InlineErrorState message={translate(READ_FAILED_I18N_KEY)} />
                        : <CourtPricingHeatmap rules={courtAvailabilities} onWeekChange={onRangeChange} />}
                </PageCard>}
            </Box>
            <Box h={4} />

            {/* Small hack to avoid re-rendering the entire component and losing some of the context/state */}
            <Box data-tour="court-calendar" opacity={viewMode === ViewMode.calendar ? 1 : 0} sx={{ '& > .chakra-card': { overflowY: 'visible' } }}>
                <PageCard
                    showBackButton
                    title={translate('court.calendar')}
                    rightHeader={isOrgManager && court ? <HStack spacing={3}>
                        <Button
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => setIsCancelDayModalVisible(true)}
                        >
                            {translate('court.cancel_day')}
                        </Button>
                        <AddAvailabilitiesModal court={court} />
                    </HStack> : null}>
                    {/* The two actions in `rightHeader` above are deliberately
                        left enabled while the month is unknown, which is the
                        judgement this slice owed. `CancelCourtDayModal` fetches
                        its OWN per-day calendar (`useGetCourtsCalendar`) and
                        previews what it is about to cancel, so it never reasons
                        from this page's read; `AddAvailabilitiesModal` takes only
                        `court`, writes through its own mutation and touches this
                        query's cache not at all. Neither can be misled by a
                        refusal here, so disabling them would remove a working
                        recovery path from the one screen that offers it. */}
                    {isCalendarUnreadable && (
                        <Box mb={4}>
                            <InlineErrorState message={translate(READ_FAILED_I18N_KEY)} />
                        </Box>
                    )}
                    <MonthlyCourtCalendar
                        onTimeSlotSelected={onTimeSlotSelected}
                        onEventSelected={(event: any) => {
                            const hasBlockade = Boolean(event.blockade);
                            const hasAvailability = Boolean(event.availability);
                            const game = (event as any).game;
                            const coachingSession = (event as any).coachingSession;
                            const eventData = (event as any).event;
                            if (hasBlockade) {
                                setSelectedBlockade(event.blockade);
                                setIsBlockadeModalVisible(true);
                            } else if (hasAvailability) {
                                setSelectedCourtAvailability(event.availability);
                                setIsCourtAvailabilityModalVisible(true);
                            } else if (game) {
                                navigate(`/admin/gameDetails?game=${game.id}`);
                            } else if (coachingSession) {
                                navigate(`/admin/coachingSessionDetails?coachingSession=${coachingSession.id}`);
                            } else if (eventData) {
                                navigate(`/admin/eventDetails?event=${eventData.id}`);
                            }
                        }}
                        backgroundEvents={backgroundEvents}
                        onRangeChange={onRangeChange}
                        events={events}
                    />
                </PageCard>
            </Box>
        </>
    );
}

/**
 * Seven calendar days either side of today. Walked with `setDate`, not
 * `± 7 * 24h`: across a clock change the arithmetic lands an hour into the
 * neighbouring day, and the `setHours` re-anchor only hides that outside the
 * 00:00–01:00 hour — opened at 00:30 the week after the October change, the
 * end boundary fell a day short. Same defect, and same fix, as the shared
 * calendar hooks.
 */
function getInitialDateBoundary() {
    const boundaryStart = new Date();
    boundaryStart.setHours(0, 0, 0, 0);
    boundaryStart.setDate(boundaryStart.getDate() - 7);
    const boundaryEnd = new Date();
    boundaryEnd.setHours(23, 59, 59, 999);
    boundaryEnd.setDate(boundaryEnd.getDate() + 7);
    return { start: boundaryStart, end: boundaryEnd };
}